<template lang="pug">
div(
  v-if="showAdvancedPricingOptionsPanel" 
  data-test-id="advanced-pricing-options-panel"
  )
  hr
  h2.pb-S Advanced pricing options
  ProductPricingAccordion(
    v-if="selectedResourceHasSegments"
    data-test-id="segment-pricing-accordion"
    title="Segment pricing"
    subtitle="Adjust the price based on customer segment."
    :statusColor="segmentPricingStatus"
    :statusTitle="segmentPricingStatusTitle"
    )
    SegmentPricing(
      :product="product"
      :disabled="disabled"
      :vuelidate="vuelidate"
      :resourceSegments="resourceSegments"
      :selectedResource="selectedResource"
      )
  ProductPricingAccordion(
    v-if="hasDeals"
    data-test-id="price-configuration-per-deal-accordion"
    title="Price configuration per deal"
    subtitle="Set price adjustments for the available deals containing this product."
    :statusColor="pricePerDealInUse ? StatusColor.green : StatusColor.gray"
    :statusTitle="pricePerDealInUse ? statusTitle.inUse : statusTitle.notInUse"
    :statusSubtitle="priceConfigurationPerDealStatusSubtitle"
    )
    PriceConfigurationPerDeal(
      :product="product"
      :disabled="disabled"
      :deals="deals"
      )
  // Only show min max price in collapsable component if you show segment pricing collapsable component.
  ProductPricingAccordion(
    v-if="selectedResourceHasSegments" 
    title="Minimum and maximum pricing"
    subtitle="Set a minimum or maximum price for the product to limit the combined impact of other pricing rules."
    :statusColor="minMaxProductPriceInUse ? StatusColor.green : StatusColor.gray"
    :statusTitle="minMaxProductPriceInUse ? statusTitle.inUse : statusTitle.notInUse"
    data-test-id="min-max-product-price"
    )
    MinMaxProductPrice(
      :product="product"
      :disabled="disabled"
      :vuelidate="vuelidate"
      )
</template>

<script>
import { pipe, prop, chain, filter, length, any, propEq, find } from 'ramda'
import { mapGetters } from 'vuex'
import ProductPricingAccordion from './AdvancedPricingOptions/ProductPricingAccordion.vue'
import PriceConfigurationPerDeal from './AdvancedPricingOptions/PriceConfigurationPerDeal.vue'
import MinMaxProductPrice from './AdvancedPricingOptions/MinMaxProductPrice.vue'
import SegmentPricing from './AdvancedPricingOptions/SegmentPricing.vue'

const StatusColor = Object.freeze({
  red: 'red',
  green: 'green',
  gray: 'gray',
})

const statusTitle = Object.freeze({
  inUse: 'In use',
  notInUse: 'Not in use',
  validationError: 'Validation error',
})

export default {
  name: 'AdvancedPricingOptions',
  components: {
    ProductPricingAccordion,
    PriceConfigurationPerDeal,
    SegmentPricing,
    MinMaxProductPrice,
  },
  props: {
    product: { type: Object, required: true },
    disabled: Boolean,
    deals: { type: Array, required: true },
    selectedResource: Object,
    resourceSegments: Array,
    vuelidate: { type: Object, required: true },
  },
  data() {
    return {
      pricePerDealInUse: false,
      StatusColor,
      statusTitle,
    }
  },
  computed: {
    ...mapGetters('briqState', ['enabledFeatures']),
    enableSegmentPricing() {
      return this.enabledFeatures.includes('SegmentPricing')
    },
    hasDeals() {
      return this.deals.length > 0
    },
    selectedResourceHasSegments() {
      if (!this.enableSegmentPricing) return false
      return this.selectedResource?.Key ? this.selectedResourceSegments?.length > 0 : false
    },
    showAdvancedPricingOptionsPanel() {
      const canDoSegmentPricing = this.selectedResourceHasSegments && this.enableSegmentPricing
      return this.hasDeals || canDoSegmentPricing // more conditions to be added later such as segment pricing etc..
    },
    priceConfigurationPerDealStatusSubtitle() {
      const getPriceOffsets = chain(d => d.Products.map(prop('PriceOffset')))
      const totalAdjustedOffsets = pipe(
        getPriceOffsets,
        filter(n => n != 0),
        length,
      )(this.deals)
      const configurationLabel = totalAdjustedOffsets > 1 ? 'configurations' : 'configuration'
      return `${totalAdjustedOffsets} ${configurationLabel}`
    },
    isSegmentPriceInUse() {
      if (!this.product.ActiveSegments) return false
      return any(p => p.PriceOffset !== null && p.PriceOffset !== 0, this.product.ActiveSegments)
    },
    segmentPricingStatus() {
      if (this.vuelidate.product?.ActiveSegments?.$error) return StatusColor.red
      return this.isSegmentPriceInUse ? StatusColor.green : StatusColor.gray
    },
    segmentPricingStatusTitle() {
      if (this.vuelidate.product?.ActiveSegments?.$error) return statusTitle.validationError
      return this.isSegmentPriceInUse ? statusTitle.inUse : statusTitle.notInUse
    },
    selectedResourceSegments() {
      return pipe(
        find(propEq('ResourceKey', this.selectedResource.Key)),
        prop('Segments'),
      )(this.resourceSegments)
    },
    minMaxProductPriceInUse() {
      return this.product.MinimumPrice || this.product.MaximumPrice
    },
  },
  methods: {
    resetSegmentPriceOffset() {
      this.product.ActiveSegments?.map(s => (s.PriceOffset = null))
    },
  },
  watch: {
    selectedResource() {
      if (this.enableSegmentPricing) this.product.ActiveSegments = this.selectedResourceSegments
    },
  },
  beforeMount() {
    const getPriceOffsets = chain(d => d.Products.map(prop('PriceOffset')))
    const allOffsets = getPriceOffsets(this.deals)
    this.pricePerDealInUse = any(p => p > 0 || p < 0)(allOffsets)
    if (!this.enableSegmentPricing) this.resetSegmentPriceOffset()
  },
}
</script>

<style scoped lang="stylus">
h2
  font-weight 400
</style>
